import React, { useState } from 'react';
import { Skeleton, Stack, Box, Typography, Collapse } from '@mui/material';

import { useGetLastestSentPaylinksQuery } from '../../../store/modules/paylink/api';
import RecentPaylink from '../RecentPaylink';
import { RefreshButton } from '../components/RefreshButton';

interface RecentPaylinksListProps {
  merchantId: string;
  merchantName: string;
}

const ListWrapper = ({
  children,
  refetchPaylinks,
}: {
  children: React.ReactNode;
  refetchPaylinks?: () => void;
}): JSX.Element => {
  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography
          component="h2"
          fontWeight="semibold"
          sx={{ fontSize: { xs: '14px', sm: '16px', md: '20px' } }}
        >
          Recently sent Paylinks
        </Typography>
        <RefreshButton onClick={refetchPaylinks} disabled={!refetchPaylinks} />
      </Box>
      {children}
    </Box>
  );
};

const RecentPaylinksList = ({ merchantId, merchantName }: RecentPaylinksListProps): JSX.Element => {
  const {
    data: paylinks,
    isFetching,
    refetch: refetchPaylinks,
  } = useGetLastestSentPaylinksQuery({ merchantId, pageSize: 5 });
  const [expandedPaylink, setExpandedPaylink] = useState<string | null>(null);

  if (isFetching) {
    return (
      <ListWrapper>
        <Stack spacing={2}>
          {new Array(5).fill(0).map((_, i) => (
            <Skeleton
              sx={{ borderRadius: '8px' }}
              variant="rectangular"
              height={115}
              data-testid="paylink-skeleton"
              // eslint-disable-next-line react/no-array-index-key
              key={i}
            />
          ))}
        </Stack>
      </ListWrapper>
    );
  }

  if (!paylinks?.length) {
    return (
      <ListWrapper refetchPaylinks={refetchPaylinks}>
        <Typography
          variant="body1"
          sx={{
            color: 'grey.600',
            fontSize: '14px',
            pt: 1,
          }}
        >
          No recent paylinks found
        </Typography>
      </ListWrapper>
    );
  }

  return (
    <ListWrapper refetchPaylinks={refetchPaylinks}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: expandedPaylink ? 0 : 2,
          overflow: 'hidden',
          transition: 'gap 0.4s',
        }}
      >
        {paylinks.map((paylink) => (
          <Collapse
            in={expandedPaylink === paylink.paylinkId || !expandedPaylink}
            key={paylink.paylinkId}
          >
            <RecentPaylink
              paylink={paylink}
              isExpanded={expandedPaylink === paylink.paylinkId}
              setIsExpanded={setExpandedPaylink}
              merchantName={merchantName}
              merchantId={merchantId}
            />
          </Collapse>
        ))}
      </Box>
    </ListWrapper>
  );
};

export default RecentPaylinksList;
